// common type declaration for themes used by emotion https://emotion.sh/docs/typescript#define-a-theme
import { getLinearGradient } from './gradients';
import { mq } from './mediaQueries';

/**
 * There is a pretty excessive type for CSS colors proposed here:
 * https://stackoverflow.com/a/68068969/707722
 * To keep it simple we type a color as a `string` for now.
 */
type Color = string;

type Intensity = 20 | 40 | 60 | 80 | 100 | 120 | 140 | 160;
type ColorWithIntensity<Prefix extends string> = {
  [K in Intensity as `${Prefix}${K}`]: Color;
};

type ColorProps = {
  brand: ColorWithIntensity<'green'> & ColorWithIntensity<'orange'>;
  accent: ColorWithIntensity<'green'> &
    ColorWithIntensity<'blue'> &
    ColorWithIntensity<'red'> &
    ColorWithIntensity<'yellow'>;
  neutral: ColorWithIntensity<'grey'> & {
    black32: Color;
    black100: Color;
    white100: Color;
  };
};

export const gradients = [
  'green',
  'orange',
  'mint',
  'mintAlt',
  'brand',
] as const;

type Gradients = (typeof gradients)[number];

type Gradient = {
  from: string;
  via?: string;
  to: string;
};

type GradientProps = { [key in Gradients]?: Gradient };

type FontSizeProps = {
  [key in Breakpoints]: {
    size: string;
    lineHeight: string;
  };
};

type FontWeightProps = {
  body: 'normal' | 'bold';
  heading: 'normal' | 'bold';
};

const breakpoints = ['xxs', 'xs', 'sm', 'md', 'lg', 'xl'] as const;

type Breakpoints = (typeof breakpoints)[number];

export const spacingKeys = [
  0, 5, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 110, 120, 130, 140, 150,
] as const;

type SpacingKeys = (typeof spacingKeys)[number];

type SpacingProps = {
  [key in SpacingKeys]: string;
};

type ElevationProps = {
  [key in 'low' | 'medium' | 'high']: string;
};

type RadiusProps = {
  [key in 'none' | 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'round']: string;
};

export interface Theme {
  colors: ColorProps;
  gradient: GradientProps;
  fontFamily: {
    default: string;
    alternate: string;
  };
  fontSize: FontSizeProps;
  fontWeight: FontWeightProps;
  radius: RadiusProps;
  elevation: ElevationProps;
  spacing: SpacingProps;
  // utility functions
  mq: typeof mq;
  linearGradient: typeof getLinearGradient;
}

export interface ThemeProps {
  theme?: Theme;
}
